import View from '@play-co/timestep-core/lib/ui/View';
import uiConfig from 'src/lib/ui/config';
import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import bitmapFonts from 'src/lib/bitmapFonts';
import sounds from 'src/lib/sounds';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import { Feature } from 'src/replicant/analytics';
import {
  ImageScaleView,
  ImageScaleViewOpts,
  ImageSliceView,
  ImageView,
  ImageViewOpts,
} from '@play-co/timestep-core/ui';
import { TextInput } from 'src/game/components/shared/TextInput';
import Checkbox from 'src/game/components/shared/Checkbox';
import { CheckboxGroup } from 'src/game/components/shared/CheckboxGroup';
import LangBitmapFontTextView, {
  LangBitmapOpts,
} from 'src/lib/ui/components/LangBitmapFontTextView';
import * as Pet from 'src/replicant/pet';
import { getTimestampText } from 'src/game/visit/visit.util';
import { duration } from 'src/replicant/utils/duration';
import * as Duo from 'src/replicant/duo';
import StateObserver from 'src/StateObserver';
import { getCreativeSuffix } from 'src/lib/utils';
import { DuoFriend } from 'src/replicant/duo/duo.asyncGetters';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import { careItems } from '../../../replicant/care/care.ruleset';
import { getPetSpeciesBucket } from 'src/replicant/pet/pet.getters';
import { GENERIC_AVATAR } from 'src/replicant/profile/assets';

import { AssetGroup } from '@play-co/timestep-core';

namespace style {
  export const profileBg = {
    image: 'assets/ui/shared/frames/scroller_panel_blue.png',
    y: 170,
    width: 520,
    height: 132,
    slices: {
      top: 19,
      left: 19,
      right: 22,
      bot: 28,
    },
  };

  export const profilePicture = {
    ...uiConfig.buruburu.invite.friendTile.image,
    width: 85,
    height: 90,
    image: GENERIC_AVATAR,
    centerOnOrigin: true,
    x: 60,
    y: 66,
  };

  export const profileName = {
    ...uiConfig.buruburu.invite.friendTile.name,
    x: 110,
    y: 25,
  };

  export const petName = {
    ...uiConfig.buruburu.invite.friendTile.name,
    color: '#BF8147',
    x: 150,
    y: 7,
  };

  export const petDetails: LangBitmapOpts = {
    size: 17,
    font: 'Body',
    color: '#BF8147',
    x: 150,
    y: 49,
    width: 190,
    height: 34,
    align: 'left',
  };

  export const itemName = {
    ...uiConfig.buruburu.invite.friendTile.name,
    color: '#BF8147',
    width: 245,
    x: 150,
    y: 7,
  };

  export const itemDetails: LangBitmapOpts = {
    size: 17,
    font: 'Body',
    color: '#BF8147',
    x: 150,
    y: 53,
    width: 240,
    height: 34,
    align: 'right',
  };

  export const responseLabel: LangBitmapOpts = {
    ...uiConfig.buruburu.invite.friendTile.name,
    color: '#BF8147',
    width: 170,
    x: 20,
    y: 600,
    verticalAlign: 'center',
  };

  export const pane = {
    ...uiConfig.buruburu.profile.profile.contentPanel,
    height: 100,
    centerOnOrigin: true,
  };

  export const visitsLabel: LangBitmapOpts = {
    size: 17,
    font: 'Body',
    color: '#7191AB',
    x: 110,
    y: 67,
    width: 190,
    height: 34,
    align: 'left',
  };

  export const lastLoginLabel: LangBitmapOpts = {
    size: 17,
    font: 'Body',
    color: '#7191AB',
    x: 298,
    y: 86,
    width: 200,
    height: 30,
    align: 'right',
    verticalAlign: 'center',
  };

  export const responseContainer = {
    ...uiConfig.buruburu.profile.profile.contentPanel,
    x: 375,
    y: 625,
    width: 370,
    height: 62,
    align: 'right',
    centerOnOrigin: true,
  } as const;

  export const messageLabel: LangBitmapOpts = {
    ...uiConfig.buruburu.invite.friendTile.name,
    color: '#BF8147',
    width: 371,
    height: 20,
    size: 20,
    x: 24,
    y: 679,
  };

  export const maxCharactersLabel: LangBitmapOpts = {
    ...uiConfig.buruburu.invite.friendTile.name,
    color: '#BF8147',
    width: 371,
    height: 20,
    size: 13,
    x: 177,
    y: 679,
    align: 'right',
    verticalAlign: 'bottom',
  };

  export const textInput = {
    y: 756,
    height: 102,
    centerOnOrigin: true,
    inputStyle: {
      background: '#d47a5a',
      boxShadow: 'inset 0px 4px 4px rgba(0, 0, 0, 0.25)',
      borderRadius: '5px',
      color: '#ffffff',
      textAlign: 'center',
      fontSize: '10vm',
      padding: '10px 5px 5px 10px',
    },
    maxLength: 48,
  };

  export const messageDisplayedLabel: LangBitmapOpts = {
    ...uiConfig.buruburu.invite.friendTile.name,
    color: '#BF8147',
    width: 420,
    height: 40,
    size: 20,
    x: 130,
    y: 813,
    wordWrap: true,
    align: 'right',
  };

  export const dashedLine: ImageViewOpts = {
    image: 'assets/ui/shared/frames/dashed_line.png',
    width: 389,
    height: 10,
    centerOnOrigin: true,
  };

  export const speciesIconBackdrop: ImageViewOpts = {
    image: 'assets/ui/shared/frames/backdrop_panel.png',
    width: 120,
    height: 120,
    x: 22,
    y: -10,
  };

  export const speciesIcon: ImageScaleViewOpts = {
    x: speciesIconBackdrop.width! / 2,
    y: speciesIconBackdrop.height! / 2,
    width: speciesIconBackdrop.width! - 10,
    height: speciesIconBackdrop.height! - 10,
    scaleMethod: 'contain',
    centerOnOrigin: true,
  };

  export const itemIcon: ImageScaleViewOpts = {
    width: 110,
    height: 120,
    x: 22,
    y: -10,
    scaleMethod: 'contain',
  };

  export const gemPanel: ImageScaleViewOpts = {
    image: 'assets/ui/shared/frames/currency_frame.png',
    scaleMethod: '9slice',
    sourceSlices: {
      horizontal: { left: 18, right: 18 },
      vertical: { top: 18, bottom: 18 },
    },
    x: 397,
    y: 40,
    width: 130,
    height: 50,
  };

  export const gemIcon: ImageScaleViewOpts = {
    scaleMethod: 'contain',
    image: 'assets/ui/hud/icons/hud_gem.png',
    width: 50,
    height: 50,
    x: 2,
  };

  export const gemText: LangBitmapOpts = {
    size: 25,
    font: 'Body',
    color: '#ffffff',
    width: 123,
    height: 50,
    align: 'right',
    verticalAlign: 'center',
  };
}

export default class PopupDuoResponse extends PopupBasic {
  private accepted: boolean | undefined = undefined;
  private profilePicture: ImageScaleView;
  private profileName: LangBitmapFontTextView;
  private visits: LangBitmapFontTextView;
  private lastLogin: LangBitmapFontTextView;
  private petName: LangBitmapFontTextView;
  private petDetails: LangBitmapFontTextView;
  private petSpecies: ImageScaleView;
  private itemExpiration: LangBitmapFontTextView;
  private itemIcon: ImageScaleView;
  private gemsReward: LangBitmapFontTextView;
  private messageInput: TextInput;
  private messageGroup: View;
  private buttonGroup: View;

  constructor(opts: {
    superview: View;
    close: (result?: { accepted: boolean; message?: string }) => void;
  }) {
    super({
      ...opts,
      superview: opts.superview,
      close: () => opts.close(),
      zIndex: 10000,
      width: uiConfig.popups.buruburu.width,
      height: 800,
      darkerBg: true,
      tracking: {
        feature: Feature.DUO._,
        $subFeature: Feature.DUO.RESPONSE_POPUP._,
      },
    });

    this.buttonClose.removeFromSuperview();

    this.message.updateOpts({
      size: 26,
      y: 72,
      width: 496,
      height: 80,
    });

    new ImageView({
      ...style.dashedLine,
      superview: this.box,
      x: this.box.style.width / 2,
      y: 160,
    });

    // Profile section.
    const profileBG = new ImageSliceView({
      ...style.profileBg,
      superview: this.box,
      x: this.box.style.width / 2 - style.profileBg.width / 2,
    });

    this.profilePicture = new ImageScaleView({
      ...style.profilePicture,
      superview: profileBG,
    });

    this.profileName = new LangBitmapFontTextView({
      ...style.profileName,
      superview: profileBG,
      width: profileBG.style.width - 268,
    });

    this.visits = new LangBitmapFontTextView({
      ...style.visitsLabel,
      superview: profileBG,
      width: profileBG.style.width - 268,
    });

    this.lastLogin = new LangBitmapFontTextView({
      ...style.lastLoginLabel,
      superview: profileBG,
    });

    // Pet section.
    const petBg = new ImageScaleView({
      ...style.pane,
      superview: this.box,
      x: this.box.style.width / 2,
      y: 380,
      width: this.box.style.width - 16,
    });

    this.petName = new LangBitmapFontTextView({
      ...style.petName,
      superview: petBg,
      width: profileBG.style.width - 268,
    });

    this.petDetails = new LangBitmapFontTextView({
      ...style.petDetails,
      superview: petBg,
      width: profileBG.style.width - 268,
    });

    const backdrop = new ImageView({
      ...style.speciesIconBackdrop,
      superview: petBg,
    });

    this.petSpecies = new ImageScaleView({
      ...style.speciesIcon,
      superview: backdrop,
      image: `assets/species/giraffe/icon.png`,
    });

    // Item section.
    const itemBG = new ImageScaleView({
      ...style.pane,
      superview: this.box,
      x: this.box.style.width / 2,
      y: 500,
      width: this.box.style.width - 16,
    });

    this.itemExpiration = new LangBitmapFontTextView({
      ...style.itemName,
      superview: itemBG,
    });

    new LangBitmapFontTextView({
      ...style.itemDetails,
      superview: itemBG,
      localeText: () => i18n('duo.response.itemReward'),
    });

    this.itemIcon = new ImageScaleView({
      ...style.itemIcon,
      superview: itemBG,
      image: uiConfig.buruburu.care.confirmation.careItemIcons.vowsCoffee,
    });

    const gemPanel = new ImageScaleView({
      ...style.gemPanel,
      superview: itemBG,
    });

    new ImageScaleView({
      ...style.gemIcon,
      superview: gemPanel,
    });

    this.gemsReward = new LangBitmapFontTextView({
      ...style.gemText,
      superview: gemPanel,
      localeText: () => 'x 100',
    });

    // Response section.
    new ImageView({
      ...style.dashedLine,
      superview: this.box,
      x: this.box.style.width / 2,
      y: 575,
    });

    new LangBitmapFontTextView({
      ...style.responseLabel,
      superview: this.box,
      localeText: () => i18n('duo.response.response'),
    });

    const responseContainer = new ImageScaleView({
      ...style.responseContainer,
      superview: this.box,
    });

    this.createResponseOptions(responseContainer);

    // Message input.

    this.messageGroup = new View({
      superview: this.box,
      infinite: true,
      canHandleEvents: false,
      visible: false,
    });

    new LangBitmapFontTextView({
      ...style.messageLabel,
      superview: this.messageGroup,
      localeText: () => i18n('duo.response.optionalMessage'),
    });

    new LangBitmapFontTextView({
      ...style.maxCharactersLabel,
      superview: this.messageGroup,
      localeText: () => i18n('duo.response.maxCharacters', { max: 48 }),
    });

    this.messageInput = new TextInput({
      ...style.textInput,
      superview: this.messageGroup,
      x: this.box.style.width / 2,
      width: this.box.style.width - 50,
      multiline: true,
    });
    this.messageInput.input.disabled = true;

    new LangBitmapFontTextView({
      ...style.messageDisplayedLabel,
      superview: this.messageGroup,
      localeText: () => i18n('duo.response.messageDisplayed'),
    });

    // Buttons section.

    this.buttonGroup = new View({
      superview: this.box,
      infinite: true,
      canHandleEvents: false,
      y: this.box.style.height - 120 / 2,
    });

    new ButtonScaleViewWithText({
      ...uiConfig.buttons.cancel,
      superview: this.buttonGroup,
      labelOffsetY: 0,
      labelOffsetX: -9,
      fontSize: 50,
      font: bitmapFonts('Body'),
      x: this.box.style.width / 2 - 255 / 2,
      width: 228,
      height: 94.7,
      centerOnOrigin: true,
      localeText: () => i18n('basic.cancel'),
      onClick: async () => {
        sounds.sfx.play('cancel');
        return opts.close();
      },
      tracking: {
        feature: Feature.DUO._,
        $subFeature: Feature.DUO.RESPONSE_POPUP.CLOSE,
      },
    });

    new ButtonScaleViewWithText({
      ...uiConfig.buttons.secondary,
      superview: this.buttonGroup,
      labelOffsetY: 0,
      labelOffsetX: -9,
      fontSize: 50,
      font: bitmapFonts('Body'),
      x: this.box.style.width / 2 + 255 / 2,
      width: 228,
      height: 94.7,
      centerOnOrigin: true,
      onClick: async () => {
        sounds.sfx.play('ok');

        if (this.accepted === undefined) {
          await openPopupPromise('popupModalWhite', {
            title: i18n('duo.response.error.title'),
            message: i18n('duo.response.error.message'),
            tracking: {
              feature: Feature.DUO._,
              $subFeature: Feature.DUO.RESPONSE_POPUP.ERROR,
            },
          });
          return;
        }

        // Remove new lines from message.
        const message = this.messageInput.text.replace(/\n/g, ' ');
        return opts.close({
          accepted: this.accepted,
          message,
        });
      },
      localeText: () => i18n('duo.response.confirm'),
      tracking: {
        feature: Feature.DUO._,
        $subFeature: Feature.DUO.RESPONSE_POPUP.CONFIRM,
      },
    });
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      'assets/ui/shared/frames/scroller_panel_blue.png',
      'assets/ui/shared/frames/dashed_line.png',
      'assets/ui/shared/frames/backdrop_panel.png',
      'assets/ui/shared/frames/currency_frame.png',
      'assets/ui/hud/icons/hud_gem.png',
      'assets/species/giraffe/icon.png',
    ]);
  }

  public init(args: DuoFriend) {
    super.init({
      title: i18n('duo.response.title'),
      message: i18n('duo.response.message'),
    });

    const { user } = StateObserver.getState();
    const evolutions = Pet.ruleset.evolutions[getPetSpeciesBucket(user)];

    this.profileName.text = args.profile.name;
    this.visits.text = i18n('duo.response.visits', {
      visits: args.profile.visits,
      visited: args.profile.visited,
    });
    this.profilePicture.setImage(args.profile.photo);
    this.lastLogin.text = `${i18n('duo.response.lastLogin')} ${getTimestampText(
      args.profile.lastLogin,
    )}`;

    this.petSpecies.setImage(`assets/species/${args.pet.species}/icon.png`);
    this.petName.text = args.pet.name;
    this.petDetails.text = i18n('duo.response.petDetails', {
      species: evolutions[args.pet.species]?.[`name_${getCreativeSuffix()}`],
      age: args.pet.age,
    });

    const elapsedTime = StateObserver.now() - args.invitation.timestamp;
    const expiration = Math.max(0, Duo.ruleset.inviteExpiration - elapsedTime);
    const hh = Math.floor(expiration / duration({ hours: 1 }));
    const mm = Math.floor(
      (expiration % duration({ hours: 1 })) / duration({ minutes: 1 }),
    );

    const { item } = careItems[args.invitation.item];
    this.itemIcon.setImage(
      uiConfig.buruburu.care.confirmation.careItemIcons[item],
    );
    this.itemExpiration.text = i18n('duo.response.expiration', {
      hh,
      mm,
    });
    this.gemsReward.text = `x ${Duo.utils.getDuoItemReward(
      args.invitation.item,
    )}`;
  }

  private createResponseOptions(parent: View) {
    const container = new View({
      superview: parent,
      x: 20,
      y: 10,
      width: 340,
      height: 50,
    });

    const checkboxes = [false, true].map((response, index) => {
      const checkbox = new Checkbox({
        superview: container,
        x: index * 170,
        y: 7,
        tracking: {
          feature: Feature.DUO.RESPONSE_POPUP._,
          $subFeature: `duo_response_popup_${response ? 'accept' : 'reject'}`,
        },
      });

      container.addSubview(
        new ButtonScaleViewWithText({
          x: index * 170 + 45,
          align: 'left',
          verticalAlign: 'center',
          fontSize: 26,
          width: 118,
          height: 46,
          labelHorizontalAlign: 'left',
          labelPaddingX: 0,
          textColor: '#BF8147',
          wordWrap: false,
          font: 'Body',
          localeText: () =>
            response
              ? i18n('duo.response.accept')
              : i18n('duo.response.reject'),
          onClick: async () => checkbox.setChecked(true),
          tracking: {
            feature: Feature.DUO.RESPONSE_POPUP._,
            $subFeature: `duo_response_popup_${response ? 'accept' : 'reject'}`,
          },
        }),
      );

      return {
        id: response,
        checkbox,
      };
    });

    return new CheckboxGroup({
      options: checkboxes,
      onSelect: (accept: boolean) => {
        sounds.sfx.play('ok');
        this.accepted = accept;
        this.messageInput.input.disabled = accept;

        const height = accept ? 800 : 975;
        this.box.updateOpts({ height });
        this.messageGroup.updateOpts({ visible: !accept });
        this.buttonGroup.updateOpts({ y: height - 120 / 2 });
      },
    });
  }
}
